import React, {
    useMemo,
    useState,
} from "react";

import { RiSparklingLine } from "@remixicon/react";
import Select from "../../../../components/select/Select";

import {
    ActionContextType,
    AiAgentType,
    ArtificialIntelligenceAgent,
    ArtificialIntelligenceAgentTool,
    GetArtificialIntelligenceObjectsDocument,
    useDeleteArtificialIntelligenceAgentMutation,
    useGetArtificialIntelligenceObjectsQuery,
    useGetWorkflowServicesQuery,
    useSaveArtificialIntelligenceAgentMutation,
} from "../../../../__generated__/api";
import { cloneWithoutTypename } from "../../../../api";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import Toggle from "../../../../components/theme/toggle/Toggle";

import Avatar from "../../../../components/avatar/Avatar";
import Popover from "../../../../components/popover/Popover";
import { useDrawerForm } from "../../../../components/theme/drawer/Drawer";
import Text from "../../../../components/theme/text/Text";
import Styles from "./ArtificialIntelligenceV3.module.scss";
import useDeleteConfirm from "../../../../components/confirm/Delete";
import createTableContainer from "../../../../components/theme/table";
import SettingsContainer from "../../../../components/theme/settings";
import Icon from "../../../../components/theme/icon/Icon";
import usePortal from "../../../../contexts/PortalContext";

export const AGENT_TYPES = [
    {
        label: "Find Answer",
        value: AiAgentType.FindAnswer,
    },
];

export const AGENT_TYPES_MAP = AGENT_TYPES.toIdMap("value");

const TableContainer = createTableContainer<ArtificialIntelligenceAgent>();

export function useDeleteAgent() {
    const [deleteAgent] = useDeleteArtificialIntelligenceAgentMutation();

    return useDeleteConfirm<ArtificialIntelligenceAgent>(item => {
        deleteAgent({
            refetchQueries: [
                { query: GetArtificialIntelligenceObjectsDocument },
            ],
            variables: {
                id: item.id,
            },
        });
    }, agent => agent.name, "Delete Agent");
}

export function useCreateAgent() {
    const [save] = useSaveArtificialIntelligenceAgentMutation();

    const [edit, setEdit] = useState<ArtificialIntelligenceAgent | undefined>(undefined);
    const { data: services, loading: isLoading } = useGetWorkflowServicesQuery();

    const servicesList = useMemo(
        () => services?.workflowServices.filter(row => row.installedIntegrationIds.isNotEmpty
                && row.actions?.some(action => action.contextTypes?.includes(ActionContextType.AiReadTool))),
        [services],
    );

    const { element, setOpen, formTracker } = useDrawerForm({
        title: "Create Agent",
        formTrackerBuilder: {
            handler: builder => builder
                .property<"id", string | undefined>("id", () => edit?.id)
                .property("name", () => edit?.name ?? "")
                .property("type", () => edit?.type ?? AiAgentType.FindAnswer)
                .property("isDefault", () => edit?.isDefault ?? true)
                .property<"availableTools", ArtificialIntelligenceAgentTool[]
            >("availableTools", () => edit?.availableTools ?? []),
            dependencies: [edit],
        },
        isLoading,
        onHide: () => {
            formTracker.reset();
            setEdit(undefined);
        },
        onSave: async () => {
            await save({
                refetchQueries: [
                    { query: GetArtificialIntelligenceObjectsDocument },
                ],
                variables: {
                    id: formTracker.properties.id.value,
                    agent: {
                        name: formTracker.properties.name.value,
                        type: formTracker.properties.type.value,
                        isDefault: formTracker.properties.isDefault.value,
                        availableTools: cloneWithoutTypename(formTracker.properties.availableTools.value) ?? [],
                    },
                },
            });
            setEdit(undefined);
            formTracker.reset();
        },

        body: tracker => (
            <>
                <FormGroup
                    label="Agent Name"
                    size="medium"
                    required
                    type="text"
                    placeholder="Agent Name"
                    value={tracker.properties.name.value}
                    onChange={tracker.properties.name.setValueFromInput}
                />
                <FormGroup
                    label="Type"
                    size="medium"
                >
                    <Select
                        options={AGENT_TYPES}
                        value={AGENT_TYPES_MAP[tracker.properties.type.value]}
                        onChange={value => {
                            tracker.properties.type.setValue(value.value as AiAgentType);
                        }}
                    />
                </FormGroup>
                <div>
                    <div className={Styles["tools-default-header"]}>
                        <Text textVariant="body-1-semibold">
                            Is Default
                            {" "}
                            <Popover
                                hasArrow
                                size="md"
                                variant="tooltip"
                                iconSize="sm"
                            >
                                This agent will be used by default for all channels.
                            </Popover>
                        </Text>
                        <Toggle
                            checked={tracker.properties.isDefault.value}
                            onChange={tracker.properties.isDefault.setCheckedFromInput}
                        />
                    </div>

                </div>
                <div className={Styles["tools-list-container"]}>
                    <div className={Styles["tools-item-header"]}>
                        <Text as="h3" textVariant="body-1-semibold">Available Tools</Text>
                        <Toggle
                            checked={tracker.properties.availableTools.value.length === servicesList?.length}
                            onChange={event => {
                                tracker.properties.availableTools.setValue(event.target.checked
                                    ? [...(servicesList?.map(service => ({ serviceId: service.id })) ?? [])]
                                    : []);
                            }}
                        />
                    </div>
                    <div className={Styles["tools-item-list"]}>

                        {servicesList?.map(service => (
                            <div key={service.id} className={Styles["tools-item"]}>
                                <Avatar src={service.squareIconUrl as string} variant="xxs" isCircle />
                                <Text
                                    textVariant="body-1-regular"
                                    className={Styles["tools-item-text"]}
                                >
                                    {service.name}
                                </Text>
                                <Toggle
                                    checked={tracker.properties.availableTools.value.some(row => row.serviceId === service.id)}
                                    onChange={event => {
                                        tracker.properties.availableTools.setValue(event.target.checked
                                            ? [...tracker.properties.availableTools.value, {
                                                serviceId: service.id,
                                            }]
                                            : tracker.properties.availableTools.value
                                                .filter(value => value.serviceId !== service.id));
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>

            </>
        ),
    });

    return {
        element,
        setOpen,
        setEdit: (current: ArtificialIntelligenceAgent) => {
            setOpen(true);
            setEdit(current);
        },
    };
}

export default function Agents({
    isEnabled,
}: {
    isEnabled: boolean
}) {
    const { entitlementIds } = usePortal();
    const { data: agentsData, loading: isAgentsLoading } = useGetArtificialIntelligenceObjectsQuery({
        variables: {
            inclueAgents: entitlementIds.has(entitlementIds.keys.ARTIFICIAL_INTELLIGENCE),
            includeMCPServers: entitlementIds.has(entitlementIds.keys.MCP_SERVERS),
        },
    });
    const { element, setOpen: setCreateAgentOpen, setEdit: setEditAgent } = useCreateAgent();
    const { element: deleteElement, setDeleting } = useDeleteAgent();

    return (
        <SettingsContainer.Section
            title="Agents"
            helpText="Agents are used to answer questions and provide information to users and customers."
        >
            <SettingsContainer.Setting
                title="Agents"
                description="Agents are used to answer questions and provide information to users and customers."
                permissions={SettingsContainer.Setting.Permissions.Administrator}
                isHideTitle
                isVisible={isEnabled}
            >
                <TableContainer
                    data={agentsData?.artificialIntelligenceAgents}
                    isLoading={isAgentsLoading}
                    borderVariant="secondary"
                >
                    <TableContainer.Empty>
                        <TableContainer.Empty.Default
                            text="Agents allow you to create custom AI agents to answer
                        questions and provide information to users and customers."
                            title="No Agents"
                            icon={<RiSparklingLine />}
                            onClick={() => {
                                setCreateAgentOpen(true);
                            }}
                            buttonText="Create Agent"
                        />
                    </TableContainer.Empty>
                    <TableContainer.TableControls>
                        <TableContainer.TableControls.Search
                            search={(query, item) => item.name?.toLowerCase().includes(query)
                        || item.type?.toString().includes(query)}
                            autoFocus={false}
                        />
                        <TableContainer.TableControls.Seperator />

                        <TableContainer.TableControls.Create
                            text="Create Agent"
                            onClick={() => {
                                setCreateAgentOpen(true);
                            }}
                        />
                    </TableContainer.TableControls>
                    <TableContainer.Table>

                        <TableContainer.TableValue
                            columnKey="name"
                            title="Name"
                        >
                            {item => item.name}
                        </TableContainer.TableValue>
                        <TableContainer.TableValue
                            columnKey="default"
                            title="Is Default"
                        >
                            {item => (item.isDefault ? "Yes" : "No")}
                        </TableContainer.TableValue>
                        <TableContainer.TableValue
                            columnKey="type"
                            title="Type"
                        >
                            {item => AGENT_TYPES_MAP[item.type ?? AiAgentType.FindAnswer]?.label}
                        </TableContainer.TableValue>

                        <TableContainer.Action
                            icon={<Icon.Edit />}
                            altText="Edit"
                            onClick={item => {
                                setEditAgent(item);
                            }}
                        />
                        <TableContainer.Action
                            icon={<Icon.Delete />}
                            altText="Delete"
                            onClick={item => {
                                setDeleting(item);
                            }}
                        />

                    </TableContainer.Table>
                </TableContainer>
            </SettingsContainer.Setting>

            {element}
            {deleteElement}

        </SettingsContainer.Section>
    );
}
